import { IntlShape } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { pickerMessages as m } from './pickerMessages';

export type PickerGroup = 'music' | 'grow' | 'links' | 'about';

export interface PickerItem {
  type: SmartLinkWidgetType;
  name: string;
  line: string;
  /** The hidden words the search reads too (design b2). */
  keywords: readonly string[];
  group: PickerGroup;
  recommended: boolean;
  /** A once-only block that the page has: faded, "On this page"; a click opens its row. */
  onPageUUID: string | null;
  /** The plan has no pre-saves: the lock chip, a click opens the plan prompt (design b3). */
  locked: boolean;
}

interface PickerItemsInput {
  isBioLink: boolean;
  canUsePresave: boolean;
  /** The name of the plan that has pre-saves, for the Free line of Catalog links. */
  presavePlanName: string | null;
  widgets: readonly SerializedWidget[];
  /** The song of the page: its Catalog links block is once-only on a song page. */
  pageSongUuid: string | null;
}

const KEYWORDS: Record<string, readonly string[]> = {
  [SmartLinkWidgetType.presave]: [
    'pre-save',
    'presave',
    'countdown',
    'streaming',
    'spotify',
    'apple',
    'release',
    'song',
  ],
  [SmartLinkWidgetType.media]: ['video', 'picture', 'image', 'reel', 'cover', 'gif', 'photo'],
  [SmartLinkWidgetType.player]: ['play', 'listen', 'audio', 'embed', 'youtube', 'video', 'spotify'],
  [SmartLinkWidgetType.presaveSubscribe]: ['subscribe', 'fans', 'email', 'follow', 'release', 'library'],
  [SmartLinkWidgetType.socialLinks]: ['instagram', 'tiktok', 'youtube', 'facebook', 'social', 'follow', 'profiles'],
  [SmartLinkWidgetType.link]: ['merch', 'tickets', 'shop', 'website', 'button', 'url', 'store'],
  [SmartLinkWidgetType.info]: ['text', 'title', 'heading', 'paragraph', 'words', 'note'],
  [SmartLinkWidgetType.profile]: ['bio', 'name', 'photo', 'avatar', 'about'],
};

function onPageUUIDOf(input: PickerItemsInput, type: SmartLinkWidgetType): string | null {
  const { widgets, isBioLink, pageSongUuid } = input;
  const found = widgets.find((widget) => {
    if (widget.type !== type) {
      return false;
    }
    if (widget.type === SmartLinkWidgetType.presave) {
      // The Bio Link holds Catalog links of many songs; a song page holds the block of its song once.
      return !isBioLink && (pageSongUuid === null || widget.data.catalogItemUuid === pageSongUuid);
    }
    // One "Get every release" per page; the Bio Link shows its Profile and Social links as there.
    if (type === SmartLinkWidgetType.presaveSubscribe) {
      return true;
    }
    return isBioLink && (type === SmartLinkWidgetType.profile || type === SmartLinkWidgetType.socialLinks);
  });
  return found?.uuid ?? null;
}

/** Every block the picker offers, in the order of the design (b1, b4). The Image block is gone. */
export function pickerItems(intl: IntlShape, input: PickerItemsInput): PickerItem[] {
  const { isBioLink, canUsePresave, presavePlanName } = input;
  let catalogLine = intl.formatMessage(m.catalogLinksLine);
  if (isBioLink) {
    catalogLine = intl.formatMessage(m.catalogLinksLineBio);
  } else if (!canUsePresave && presavePlanName !== null) {
    catalogLine = intl.formatMessage(m.catalogLinksLineFree, { plan: presavePlanName });
  }
  const recommended = isBioLink
    ? [SmartLinkWidgetType.presave, SmartLinkWidgetType.presaveSubscribe, SmartLinkWidgetType.link]
    : [SmartLinkWidgetType.presave, SmartLinkWidgetType.media, SmartLinkWidgetType.player];
  const make = (type: SmartLinkWidgetType, name: string, line: string, group: PickerGroup): PickerItem => ({
    type,
    name,
    line,
    keywords: KEYWORDS[type] ?? [],
    group,
    recommended: recommended.includes(type),
    onPageUUID: onPageUUIDOf(input, type),
    // Catalog links is never locked: streaming links are free; only its pre-save needs a plan.
    locked: type === SmartLinkWidgetType.presaveSubscribe && !canUsePresave,
  });

  return [
    make(
      SmartLinkWidgetType.presave,
      intl.formatMessage(isBioLink ? m.catalogLinksBio : m.catalogLinks),
      catalogLine,
      'music'
    ),
    make(SmartLinkWidgetType.player, intl.formatMessage(m.player), intl.formatMessage(m.playerLine), 'music'),
    make(
      SmartLinkWidgetType.presaveSubscribe,
      intl.formatMessage(m.subscribe),
      intl.formatMessage(m.subscribeLine),
      'grow'
    ),
    make(SmartLinkWidgetType.socialLinks, intl.formatMessage(m.social), intl.formatMessage(m.socialLine), 'grow'),
    make(SmartLinkWidgetType.link, intl.formatMessage(m.link), intl.formatMessage(m.linkLine), 'links'),
    make(SmartLinkWidgetType.media, intl.formatMessage(m.media), intl.formatMessage(m.mediaLine), 'links'),
    make(SmartLinkWidgetType.info, intl.formatMessage(m.text), intl.formatMessage(m.textLine), 'links'),
    make(SmartLinkWidgetType.profile, intl.formatMessage(m.profile), intl.formatMessage(m.profileLine), 'about'),
  ];
}

/** The search of the picker (design b2): the name, the line and the hidden words. */
export function searchPickerItems(items: readonly PickerItem[], query: string): PickerItem[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return [...items];
  }
  return items.filter(
    (item) =>
      item.name.toLowerCase().includes(needle) ||
      item.line.toLowerCase().includes(needle) ||
      item.keywords.some((word) => word.includes(needle))
  );
}
